"use client";
import type { Session, User } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { parseRedirectTo } from "./lib/redirect";
import { supabase } from "./lib/supabase";

export { supabase } from "./lib/supabase";

export const useUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes.
    // No manual server sync needed — the browser client now uses @supabase/ssr
    // cookie storage, so tokens are written to cookies automatically. Middleware
    // reads these same cookies and refreshes tokens on each request.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, session, loading };
};

export const signIn = (email: string, password: string): Promise<any> => supabase.auth.signInWithPassword({ email, password });

export const signInWithOAuth = (provider: "google" | "azure"): Promise<any> => {
  const options: any = provider === "azure" ? { scopes: "email" } : {};

  // Ensure OAuth returns to our client callback handler which completes the session
  try {
    if (typeof window !== "undefined") {
      const origin = window.location.origin;
      const params = new URLSearchParams(window.location.search);
      // Checked here as well as on the callback page, so an address pointing
      // somewhere else never rides along to the identity provider and back.
      const redirectToParam = parseRedirectTo(params.get("redirectTo"));

      const callbackUrl = new URL("/auth/callback", origin);
      if (redirectToParam) {
        callbackUrl.searchParams.set("redirect_to", `${redirectToParam.pathname}${redirectToParam.search}`);
      }

      options.redirectTo = callbackUrl.toString();
    } else {
      // Server-side fallback: check env vars including Vercel's auto-generated URL
      const base =
        process.env.NEXT_PUBLIC_SITE_URL || process.env.NEXT_PUBLIC_APP_URL || (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : "");
      if (base) {
        options.redirectTo = `${base.replace(/\/$/, "")}/auth/callback`;
      }
    }
  } catch (_) {
    // Non-fatal; fall back to default Supabase redirect if any
  }

  return supabase.auth.signInWithOAuth({ provider, options });
};

export const signUp = (email: string, password: string): Promise<any> => supabase.auth.signUp({ email, password });

export const signOut = async (): Promise<any> => {
  try {
    // signOut() clears the session from the @supabase/ssr cookie storage.
    // Middleware will see the cleared cookies on the next request.
    const { error } = await supabase.auth.signOut();

    if (typeof window !== "undefined") {
      window.location.href = "/sign-in";
    }

    return { error };
  } catch (err) {
    console.error("Sign out error:", err);
    return { error: err };
  }
};

export * from "./components";

// Export organization context
export * from "./contexts/organization";
